import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Package, ArrowLeft } from "lucide-react";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/shop/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14 space-y-8" data-testid="orders-page">
      <div>
        <Link to="/app/shop" className="font-mono text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-black inline-flex items-center gap-1" data-testid="orders-back">
          <ArrowLeft className="w-3 h-3" /> BACK TO SHOP
        </Link>
        <div className="eyebrow mt-4">ORDER HISTORY</div>
        <h1 className="font-display text-5xl md:text-6xl uppercase mt-2 leading-none">Every stack, every drop.</h1>
      </div>

      {loading ? (
        <div className="eyebrow">LOADING…</div>
      ) : orders.length === 0 ? (
        <div className="panel p-10 rounded-2xl text-center" data-testid="orders-empty">
          <Package className="w-10 h-10 mx-auto mb-4 text-neutral-400" />
          <div className="font-display text-3xl uppercase mb-2">No orders yet</div>
          <p className="text-neutral-600 mb-6">Your first stack is one checkout away.</p>
          <Link to="/app/shop" className="btn-lime">Browse the Shop</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="panel p-6 rounded-2xl" data-testid={`order-${o.id}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    ORDER · {o.id.slice(-8).toUpperCase()}
                  </div>
                  <div className="font-mono text-xs text-neutral-400 mt-1">
                    {new Date(o.created_at).toLocaleString()}
                  </div>
                </div>
                <StatusPill status={o.status} />
              </div>
              <div className="space-y-1 text-sm text-neutral-700 mb-3">
                {o.items.map((it, i) => (
                  <div key={`${o.id}-${it.product_id || i}-${it.variant || ""}`} className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{it.name || it.product_id}</span>
                      <span className="text-neutral-500 ml-2 font-mono text-[10px] uppercase tracking-widest">
                        {it.variant || "30day"} × {it.quantity}
                        {it.subscription ? " · SUB" : ""}
                      </span>
                    </div>
                    <span className="font-mono">${((it.unit_price || 0) * (it.quantity || 1)).toFixed(0)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between border-t border-neutral-200 pt-3">
                <span className="font-mono text-xs uppercase tracking-widest text-neutral-500">Total</span>
                <span className="font-display text-2xl">${o.total.toFixed(0)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: { label: "PAID", bg: "bg-lime", text: "text-obsidian" },
    initiated: { label: "PENDING", bg: "bg-neutral-200", text: "text-neutral-700" },
    shipped: { label: "SHIPPED", bg: "bg-obsidian", text: "text-white" },
    cancelled: { label: "CANCELLED", bg: "bg-neutral-100", text: "text-neutral-500" },
    expired: { label: "EXPIRED", bg: "bg-neutral-100", text: "text-neutral-500" },
  };
  const m = map[status] || map.initiated;
  return <span className={`text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-full ${m.bg} ${m.text}`}>{m.label}</span>;
}
